import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import { webviewContextAtom, optimisticWebviewContextAtom } from '@renderer/chat'

export type SidePanelContentType = 'audio-note' | 'chat' | 'insight' | 'webview' | null

const sidePanelOpenAtom = atomWithGlobalStore<boolean>('chat', 'side-panel-open', false)
export const useSidePanelOpen = () => useAtomValue(sidePanelOpenAtom)
export const useSetSidePanelOpen = () => useSetAtom(sidePanelOpenAtom)

export const memorySidePanelOpenAtom = atom<boolean>((get) => {
  return get(sidePanelOpenAtom)
})
export const useMemorySidePanelOpen = () => useAtomValue(memorySidePanelOpenAtom)

export const sidePanelContentTypeAtom = atomWithGlobalStore<SidePanelContentType>(
  'chat',
  'side-panel-content-type',
  null,
)
export const useSidePanelContentType = () => useAtomValue(sidePanelContentTypeAtom)
export const useSetSidePanelContentType = () => useSetAtom(sidePanelContentTypeAtom)

export const selectedAudioNoteIdAtom = atom(null as string | null, (_, set, value: string | null) => {
  set(selectedAudioNoteIdAtom, value)
  if (value) {
    set(sidePanelContentTypeAtom, 'audio-note')
  }
})
export const useSelectedAudioNoteId = () => useAtomValue(selectedAudioNoteIdAtom)
export const useSetSelectedAudioNoteId = () => useSetAtom(selectedAudioNoteIdAtom)

export const selectedChatIdAtom = atom('' as string, (_, set, chatId: string) => {
  set(selectedChatIdAtom, chatId)
  if (chatId) {
    set(sidePanelContentTypeAtom, 'chat')
  }
})

export const selectedInsightIdAtom = atom(null as string | null, (_, set, value: string | null) => {
  set(selectedInsightIdAtom, value)
  if (value) {
    set(sidePanelContentTypeAtom, 'insight')
  }
})

export const useSelectedInsightId = () => useAtomValue(selectedInsightIdAtom)
export const useSetSelectedInsightId = () => useSetAtom(selectedInsightIdAtom)

const selectedWebviewUrlAtom = atom('' as string, (_, set, url: string) => {
  set(selectedWebviewUrlAtom, url)
  if (url) {
    set(sidePanelContentTypeAtom, 'webview')
    // set optimistic context immediately when webview opens
    let domain = url
    try {
      domain = new URL(url).hostname
    } catch (error) {
      console.error('Failed to parse webview URL:', url, error)
    }
    set(optimisticWebviewContextAtom, {
      url,
      title: domain,
      isLoading: true,
    })
  } else {
    // Reset navigation when webview is closed
    set(webviewNavigationAtom, defaultWebviewNavigation)
    // Reset content type and close side panel when no webview URL
    set(sidePanelContentTypeAtom, null)
    // Clear webview context when webview URL is reset
    set(webviewContextAtom, null)
    set(optimisticWebviewContextAtom, null)
  }
})
export const useSelectedWebviewUrl = () => useAtomValue(selectedWebviewUrlAtom)
export const useSetSelectedWebviewUrl = () => useSetAtom(selectedWebviewUrlAtom)

// Webview navigation atoms
interface WebviewNavigation {
  goBack: () => void
  goForward: () => void
  reload: () => void
  canGoBack: boolean
  canGoForward: boolean
  currentUrl: string
}

const defaultWebviewNavigation: WebviewNavigation = {
  goBack: () => {},
  goForward: () => {},
  reload: () => {},
  canGoBack: false,
  canGoForward: false,
  currentUrl: '',
}

export const webviewNavigationAtom = atom<WebviewNavigation>(defaultWebviewNavigation)
export const useWebviewNavigation = () => useAtomValue(webviewNavigationAtom)
export const useSetWebviewNavigation = () => useSetAtom(webviewNavigationAtom)

interface WebviewChatState {
  url: string
  chatId: string
  explicitlyClosed: boolean
}

const webviewChatStateAtom = atom(new Map<string, WebviewChatState>())

// Helper atom to open webview for a specific chat
export const openWebviewForChatAtom = atom(null, (get, set, { url, chatId }: { url: string; chatId: string }) => {
  const currentState = get(webviewChatStateAtom)
  const newState = new Map(currentState)

  newState.set(chatId, { url, chatId, explicitlyClosed: false })
  set(webviewChatStateAtom, newState)
  set(selectedWebviewUrlAtom, url)
})

// Helper atom to explicitly close webview
export const closeWebviewExplicitlyAtom = atom(null, (get, set, chatId: string) => {
  const currentState = get(webviewChatStateAtom)
  const newState = new Map(currentState)
  const existingState = newState.get(chatId)

  if (existingState) {
    newState.set(chatId, { ...existingState, explicitlyClosed: true })
    set(webviewChatStateAtom, newState)
  }
  set(selectedWebviewUrlAtom, '')
  set(webviewContextAtom, null)
})

// Helper atom to restore webview for a chat
export const restoreWebviewForChatAtom = atom(null, (get, set, chatId: string) => {
  const currentState = get(webviewChatStateAtom)
  const chatWebviewState = currentState.get(chatId)

  if (chatWebviewState && !chatWebviewState.explicitlyClosed) {
    set(selectedWebviewUrlAtom, chatWebviewState.url)
    set(sidePanelOpenAtom, true)
  } else {
    // No webview for this chat, so close everything
    set(selectedWebviewUrlAtom, '')
    set(sidePanelContentTypeAtom, null)
    set(sidePanelOpenAtom, false)
  }
})

export const useOpenWebviewForChat = () => useSetAtom(openWebviewForChatAtom)
export const useCloseWebviewExplicitly = () => useSetAtom(closeWebviewExplicitlyAtom)
export const useRestoreWebviewForChat = () => useSetAtom(restoreWebviewForChatAtom)
